/**
 * Runtime APIs pdf.js 6 calls that Chrome 92 lacks. Funeral home screens run
 * Chrome 92 (Debian buster, cannot be upgraded): without these, every PDF
 * fails to load there ("Promise.withResolvers is not a function").
 *
 * The pdf.js legacy build only polyfills the APIs newer than its own baseline
 * (iterator helpers, Uint8Array base64, Math.sumPrecise…), not these older
 * ones. Imported by `pdfjs.ts` on the page and by `pdfjs.worker.ts` in the
 * worker, which has its own globals. Each polyfill is a no-op where the
 * browser already has the API.
 *
 * The syntax side (class `static {}` blocks) is handled by `build.target`
 * (vite.config.ts).
 */
import 'core-js/stable/promise/with-resolvers'
import 'core-js/stable/object/has-own'
import 'core-js/stable/structured-clone'
import 'core-js/stable/array/find-last'
import 'core-js/stable/array/to-reversed'
import 'core-js/stable/array-buffer/detached'
import 'core-js/stable/array-buffer/transfer-to-fixed-length'

type AbortSignalWithAny = typeof AbortSignal & {
  any?: (signals: AbortSignal[]) => AbortSignal
}

// Chrome 116+. Not in core-js.
const abortSignal = AbortSignal as AbortSignalWithAny

if (typeof abortSignal.any !== 'function') {
  abortSignal.any = (signals: AbortSignal[]): AbortSignal => {
    const controller = new AbortController()
    const alreadyAborted = signals.find((signal) => signal.aborted)

    if (alreadyAborted) {
      controller.abort(alreadyAborted.reason)
      return controller.signal
    }

    for (const signal of signals) {
      signal.addEventListener('abort', () => controller.abort(signal.reason), {
        once: true,
        signal: controller.signal
      })
    }

    return controller.signal
  }
}

// `for await (const chunk of readableStream)`: Chrome 124+. Not in core-js.
// The DOM lib declares the native iterator, which may be missing at runtime.
const streamPrototype = ReadableStream.prototype as unknown as {
  [Symbol.asyncIterator]?: (
    this: ReadableStream
  ) => AsyncIterableIterator<unknown>
}

if (typeof streamPrototype[Symbol.asyncIterator] !== 'function') {
  streamPrototype[Symbol.asyncIterator] = function (
    this: ReadableStream
  ): AsyncIterableIterator<unknown> {
    const reader = this.getReader()

    return {
      async next() {
        const result = await reader.read()

        if (result.done) {
          reader.releaseLock()
        }

        return result as IteratorResult<unknown>
      },
      // Leaving the loop early (break, throw) cancels the stream, like the
      // native iterator does.
      async return(value?: unknown) {
        const cancelled = reader.cancel(value)
        reader.releaseLock()
        await cancelled

        return { done: true, value }
      },
      [Symbol.asyncIterator]() {
        return this
      }
    }
  }
}
